import React from 'react';

export interface LegacyBallInfoResponse {
  event: {
    date: string;
    startTime: string;
    endTime: string;
    venue: string;
    location: string;
    dressCode: string;
    scheduleHighlights: string[];
  } | null;
  tickets: {
    tiers: { name: string; price: number; description: string }[];
    capacity: number;
    sold: number;
    available: number;
    complimentary?: number;
  };
  mealOptions: { name: string; description: string }[];
}

interface LegacyBallInfoViewProps {
  data: LegacyBallInfoResponse | null;
}

export function calculateDaysRemaining(eventDate: string): number {
  const now = new Date();
  const event = new Date(eventDate);
  const diffMs = event.getTime() - now.getTime();
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  // Parse the date portion directly (stored as Eastern, no conversion needed)
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day); // Local date, no UTC shift
  return d.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  // Extract HH:MM from stored value (stored as Eastern, no conversion needed)
  let hours: number;
  let minutes: number;

  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }

  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

const styles: Record<string, React.CSSProperties> = {
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1.25rem',
    marginBottom: '2rem',
  },
  card: {
    padding: '1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardTitle: {
    color: '#0f2d1f',
    fontSize: '0.85rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
    fontWeight: 600,
  },
  cardValue: {
    fontSize: '1.1rem',
    color: '#333',
    lineHeight: 1.5,
  },
  countdownCard: {
    padding: '1.5rem',
    backgroundColor: '#1a6b3c',
    border: '1px solid #1a6b3c',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    textAlign: 'center' as const,
    gridColumn: '1 / -1',
  },
  countdownValue: {
    fontSize: '2.5rem',
    fontWeight: 700,
    color: '#fff',
    marginBottom: '0.25rem',
  },
  countdownLabel: {
    fontSize: '0.9rem',
    color: 'rgba(255,255,255,0.85)',
  },
  tierItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  tierName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.95rem',
  },
  tierPrice: {
    fontWeight: 700,
    color: '#1a6b3c',
    fontSize: '1rem',
  },
  tierDesc: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.15rem',
  },
  availabilityBar: {
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '0.75rem',
  },
  availabilityFill: {
    height: '100%',
    backgroundColor: '#1a6b3c',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  mealItem: {
    padding: '0.5rem 0',
    borderBottom: '1px solid #f0f0f0',
  },
  mealName: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.95rem',
  },
  mealDesc: {
    fontSize: '0.8rem',
    color: '#666',
    marginTop: '0.15rem',
  },
  placeholder: {
    color: '#999',
    fontStyle: 'italic',
    fontSize: '0.95rem',
  },
  highlightItem: {
    padding: '0.35rem 0',
    fontSize: '0.95rem',
    color: '#333',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  highlightBullet: {
    color: '#c9a84c',
    fontWeight: 700,
    fontSize: '1rem',
    lineHeight: '1.4',
  },
};

function PlaceholderCard({ title }: { title: string }) {
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>{title}</div>
      <div style={styles.placeholder}>Details coming soon</div>
    </div>
  );
}

export function LegacyBallInfoView({ data }: LegacyBallInfoViewProps) {
  if (!data) {
    return (
      <div style={styles.grid}>
        <PlaceholderCard title="Event Date & Time" />
        <PlaceholderCard title="Venue" />
        <PlaceholderCard title="Dress Code" />
        <PlaceholderCard title="Schedule Highlights" />
        <PlaceholderCard title="Ticket Pricing" />
        <PlaceholderCard title="Ticket Availability" />
        <PlaceholderCard title="Meal Options" />
      </div>
    );
  }

  const { event, tickets, mealOptions } = data;
  const daysRemaining = event ? calculateDaysRemaining(event.date) : null;

  return (
    <div>
      {/* Countdown Timer */}
      {event && daysRemaining !== null && daysRemaining >= 0 && (
        <div style={styles.countdownCard}>
          <div style={styles.countdownValue}>{daysRemaining}</div>
          <div style={styles.countdownLabel}>
            {daysRemaining === 1 ? 'day until the Legacy Ball' : 'days until the Legacy Ball'}
          </div>
        </div>
      )}

      <div style={{ ...styles.grid, marginTop: event && daysRemaining !== null && daysRemaining >= 0 ? '1.25rem' : '0' }}>
        {/* Event Date & Time Card */}
        {event ? (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Event Date & Time</div>
            <div style={styles.cardValue}>
              <div style={{ fontWeight: 600 }}>{formatDate(event.date)}</div>
              <div style={{ marginTop: '0.25rem', color: '#666' }}>
                {formatTime(event.startTime)} – {formatTime(event.endTime)}
              </div>
            </div>
          </div>
        ) : (
          <PlaceholderCard title="Event Date & Time" />
        )}

        {/* Venue Card */}
        {event ? (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Venue</div>
            <div style={styles.cardValue}>
              <div style={{ fontWeight: 600 }}>{event.venue}</div>
              <div style={{ marginTop: '0.25rem', color: '#666' }}>{event.location}</div>
            </div>
          </div>
        ) : (
          <PlaceholderCard title="Venue" />
        )}

        {/* Dress Code Card */}
        {event ? (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Dress Code</div>
            <div style={{ ...styles.cardValue, fontWeight: 600, color: '#1a6b3c' }}>
              {event.dressCode}
            </div>
          </div>
        ) : (
          <PlaceholderCard title="Dress Code" />
        )}

        {/* Schedule Highlights Card */}
        {event ? (
          <div style={styles.card}>
            <div style={styles.cardTitle}>Schedule Highlights</div>
            <div style={styles.cardValue}>
              {event.scheduleHighlights.length > 0 ? (
                event.scheduleHighlights.map((item, idx) => (
                  <div key={idx} style={styles.highlightItem}>
                    <span style={styles.highlightBullet}>•</span>
                    <span>{item}</span>
                  </div>
                ))
              ) : (
                <span style={styles.placeholder}>Schedule to be announced</span>
              )}
            </div>
          </div>
        ) : (
          <PlaceholderCard title="Schedule Highlights" />
        )}

        {/* Ticket Pricing Card */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Ticket Pricing</div>
          <div style={styles.cardValue}>
            {tickets.tiers.length > 0 ? (
              tickets.tiers.map((tier, idx) => (
                <div key={idx} style={styles.tierItem}>
                  <div>
                    <div style={styles.tierName}>{tier.name}</div>
                    <div style={styles.tierDesc}>{tier.description}</div>
                  </div>
                  <div style={styles.tierPrice}>${tier.price.toFixed(2)}</div>
                </div>
              ))
            ) : (
              <span style={styles.placeholder}>Pricing to be announced</span>
            )}
          </div>
        </div>

        {/* Ticket Availability Card */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Ticket Availability</div>
          <div style={styles.cardValue}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1a6b3c' }}>
              {tickets.available}
            </div>
            <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              tickets remaining of {tickets.capacity}
            </div>
            <div style={styles.availabilityBar}>
              <div
                style={{
                  ...styles.availabilityFill,
                  width: tickets.capacity > 0
                    ? `${((tickets.sold + (tickets.complimentary || 0)) / tickets.capacity) * 100}%`
                    : '0%',
                }}
              />
            </div>
            <div style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              {tickets.sold} paid
              {(tickets.complimentary || 0) > 0 && (
                <span style={{ marginLeft: '0.5rem', color: '#1e40af' }}>
                  + {tickets.complimentary} comp 🎁
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Meal Options Card */}
        <div style={styles.card}>
          <div style={styles.cardTitle}>Meal Options</div>
          <div style={styles.cardValue}>
            {mealOptions.length > 0 ? (
              mealOptions.map((meal, idx) => (
                <div key={idx} style={styles.mealItem}>
                  <div style={styles.mealName}>{meal.name}</div>
                  <div style={styles.mealDesc}>{meal.description}</div>
                </div>
              ))
            ) : (
              <span style={styles.placeholder}>Menu to be announced</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
